/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: tabs/AdvancedTab.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Advanced tab component for Conference form - Presence & Integration
────────────────────────────────────────────────────────────────*/

import { Zap } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { useDebugMode } from '../../../hooks/useDebugMode';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Toggle } from '../../../components/ui/Toggle';
import { FormBox } from '../../../components/ui/FormBox';
import type { ConferenceRoom } from '../types';

interface AdvancedTabProps {
  formData: ConferenceRoom;
  onChange: (field: keyof ConferenceRoom, value: ConferenceRoom[keyof ConferenceRoom]) => void;
}

export function AdvancedTab({ formData, onChange }: AdvancedTabProps) {
  const { t } = useI18n();
  const { isDebugModeActive } = useDebugMode();

  const buildTooltipContent = (tooltipKey: string, debugKey?: string) => {
    const base = t(tooltipKey);
    if (!debugKey || !isDebugModeActive) {
      return base;
    }
    return `${base}\n\n${t('conferences:debug.field')}: ${t(debugKey)}`;
  };

  return (
    <div className="space-y-3">
      <FormBox
        title={t('conferences:sections.advanced')}
        icon={<Zap className="h-5 w-5" />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
          {/* Row 1 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.presenceId', 'conferences:debug.presence_id')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.presenceId')}
            </label>
          </SimpleTooltip>
          <input
            type="text"
            value={formData.presenceId}
            onChange={(e) => onChange('presenceId', e.target.value)}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            placeholder="${conference_name}@${domain}"
          />
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.announceNewModerator', 'conferences:debug.announce_new_moderator')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.announceNewModerator')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.announceNewModerator}
              onChange={(value) => onChange('announceNewModerator', value)}
            />
          </div>

          {/* Row 2 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.apiControlViaEventSocket', 'conferences:debug.api_control')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.apiControlViaEventSocket')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.apiControlViaEventSocket}
              onChange={(value) => onChange('apiControlViaEventSocket', value)}
            />
          </div>
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.databaseLogging', 'conferences:debug.database_logging')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.databaseLogging')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.databaseLogging}
              onChange={(value) => onChange('databaseLogging', value)}
            />
          </div>

          {/* Row 3 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.dtmfMenuKey', 'conferences:debug.dtmf_menu')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.dtmfMenuKey')}
            </label>
          </SimpleTooltip>
          <input
            type="text"
            value={formData.dtmfMenuKey}
            onChange={(e) => onChange('dtmfMenuKey', e.target.value)}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            placeholder="*"
            maxLength={1}
          />
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.moderatorControls', 'conferences:debug.moderator_controls')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.moderatorControls')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.moderatorControls}
              onChange={(value) => onChange('moderatorControls', value)}
            />
          </div>
        </div>
      </FormBox>
    </div>
  );
}

